import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/auth/AuthProvider";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Users,
  Sparkles,
  FileSignature,
  HardHat,
  MapPin,
  Package,
  Clock,
  Wallet,
  Receipt,
  FileText,
  TrendingUp,
  ShieldCheck,
  ArrowRight,
  CheckCircle2,
  Info,
  Lightbulb,
  Search,
  Settings,
  LayoutDashboard,
  Satellite,
  Download,
  Bell,
  Palette,
  Trash2,
  FileBarChart,
  Smartphone,
  UserPlus,
  Building2,
  LogIn,
  CalendarX,
  CreditCard,
  BarChart3,
  LifeBuoy,
  AlarmClock,
  MessageCircle,
  Map as MapIcon,
  Eye,
  Upload,
  Notebook,
} from "lucide-react";

export const Route = createFileRoute("/_app/help")({
  component: HelpPage,
});

/* ── icon map for categories ── */
const CATEGORY_ICONS: Record<string, typeof Users> = {
  gettingStarted: Lightbulb,
  sales: Receipt,
  operations: HardHat,
  workforce: Clock,
  finance: Wallet,
  gps: Satellite,
  settings: Settings,
  dashboard: LayoutDashboard,
};

/* ── icon map for items ── */
const ITEM_ICONS: Record<string, typeof Users> = {
  register: LogIn,
  companySetup: Building2,
  team: UserPlus,
  pwa: Smartphone,
  clients: Users,
  estimates: Sparkles,
  contracts: FileSignature,
  invoices: Receipt,
  projects: HardHat,
  jobSites: MapPin,
  materials: Package,
  timeClock: Clock,
  absences: CalendarX,
  unpaidHours: CreditCard,
  payroll: Wallet,
  expenses: FileText,
  cashFlow: TrendingUp,
  reports: FileBarChart,
  gpsAnalytics: Satellite,
  gpsReports: BarChart3,
  gpsConfig: Settings,
  pdfBranding: Palette,
  estimateTemplate: FileText,
  chartThemes: Palette,
  alerts: Bell,
  backup: Download,
  import: Upload,
  deleteAccount: Trash2,
  overview: LayoutDashboard,
  plans: CreditCard,
  support: LifeBuoy,
  clockSchedule: AlarmClock,
  clockAlerts: Bell,
  changeOrders: FileSignature,
  invoiceClientView: Eye,
  autoClockOut: AlarmClock,
  payType: Wallet,
  employeeNotes: Notebook,
  expensesDragDrop: Upload,
  liveSitesMap: MapIcon,
  whatsapp: MessageCircle,
  supportPresence: LifeBuoy,
  ownerSupport: LogIn,
};

type Access = "all" | "supervisor" | "admin" | "owner";

/* ── nível de acesso por categoria (owner ⊇ admin ⊇ supervisor ⊇ all) ── */
const CATEGORY_ACCESS: Record<string, Access> = {
  gettingStarted: "all",
  sales: "admin",
  operations: "supervisor",
  workforce: "all",
  finance: "supervisor",
  gps: "supervisor",
  settings: "admin",
  dashboard: "admin",
};

/* ── nível de acesso por item; herda o da categoria quando ausente ── */
const ITEM_ACCESS: Record<string, Access> = {
  // gettingStarted
  register: "admin",
  companySetup: "admin",
  team: "admin",
  pwa: "all",
  support: "all",
  supportPresence: "all",
  // sales
  clients: "admin",
  estimates: "admin",
  contracts: "admin",
  invoices: "admin",
  changeOrders: "admin",
  invoiceClientView: "admin",
  // operations
  projects: "admin",
  jobSites: "supervisor",
  materials: "supervisor",
  clockSchedule: "admin",
  // workforce
  timeClock: "all",
  absences: "admin",
  unpaidHours: "admin",
  payroll: "admin",
  clockAlerts: "supervisor",
  autoClockOut: "all",
  payType: "admin",
  employeeNotes: "admin",
  // finance
  expenses: "supervisor",
  cashFlow: "admin",
  reports: "admin",
  expensesDragDrop: "supervisor",
  // gps
  gpsAnalytics: "supervisor",
  gpsReports: "admin",
  gpsConfig: "admin",
  liveSitesMap: "supervisor",
  // settings
  pdfBranding: "admin",
  estimateTemplate: "admin",
  chartThemes: "admin",
  alerts: "admin",
  backup: "admin",
  import: "admin",
  deleteAccount: "admin",
  whatsapp: "admin",
  // dashboard
  overview: "admin",
  plans: "admin",
  ownerSupport: "owner",
};

const TIP_KEYS = [
  { key: "linkProject", icon: Info },
  { key: "immutable", icon: ShieldCheck },
  { key: "useAI", icon: Lightbulb },
  { key: "approvals", icon: CheckCircle2 },
  { key: "backup", icon: Download },
  { key: "gpsFirst", icon: Satellite },
] as const;

const CATEGORY_ORDER = [
  "gettingStarted",
  "sales",
  "operations",
  "workforce",
  "finance",
  "gps",
  "settings",
  "dashboard",
] as const;

type HelpItem = {
  title: string;
  desc: string;
  details: string[];
  link?: string;
};

type HelpCategory = {
  title: string;
  items: Record<string, HelpItem>;
};

function HelpPage() {
  const { t } = useTranslation();
  const { hasRole } = useAuth();
  const isOwner = hasRole("owner");
  const isAdmin = hasRole("admin");
  const isSupervisor = hasRole("supervisor");
  // owner ⊇ admin ⊇ supervisor ⊇ all — mesma hierarquia do guard de rota em _app.tsx.
  const reaches = (access: Access = "all"): boolean => {
    if (isOwner) return true;
    switch (access) {
      case "all":
        return true;
      case "supervisor":
        return isAdmin || isSupervisor;
      case "admin":
        return isAdmin;
      case "owner":
        return false;
    }
  };
  const [search, setSearch] = useState("");

  if (typeof document !== "undefined") {
    document.title = t("help.metaTitle");
  }

  /* ── build categories from i18n, scoped to the user's role ── */
  const categories = useMemo(() => {
    return CATEGORY_ORDER.map((catKey) => {
      const catAccess = CATEGORY_ACCESS[catKey] ?? "all";
      if (!reaches(catAccess)) return null;
      const cat = t(`help.categories.${catKey}`, { returnObjects: true }) as HelpCategory;
      if (!cat || typeof cat !== "object" || !cat.items) return null;
      const items = Object.entries(cat.items)
        .filter(([itemKey]) => reaches(ITEM_ACCESS[itemKey] ?? catAccess))
        .map(([itemKey, item]) => ({ key: itemKey, ...item }));
      if (items.length === 0) return null;
      return { key: catKey, title: cat.title, items };
    }).filter(Boolean) as Array<{
      key: string;
      title: string;
      items: Array<{ key: string } & HelpItem>;
    }>;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, isOwner, isAdmin, isSupervisor]);

  /* ── filter by search ── */
  const query = search.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!query) return categories;
    return categories
      .map((cat) => {
        const matchingItems = cat.items.filter(
          (item) =>
            item.title.toLowerCase().includes(query) ||
            item.desc.toLowerCase().includes(query) ||
            item.details.some((d) => d.toLowerCase().includes(query)),
        );
        if (matchingItems.length === 0) return null;
        return { ...cat, items: matchingItems };
      })
      .filter(Boolean) as typeof categories;
  }, [categories, query]);

  return (
    <div className="mx-auto max-w-5xl space-y-8 p-4 sm:p-6">
      {/* Header */}
      <div className="space-y-4">
        <Badge variant="outline" className="border-primary/40 text-primary">
          {t("help.badge")}
        </Badge>
        <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">{t("help.title")}</h1>
        <p className="text-muted-foreground">{t("help.subtitle")}</p>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("help.searchPlaceholder")}
            className="pl-10"
          />
        </div>
      </div>

      {/* Quick tips (admin+ apenas — dicas de backup/IA/GPS não se aplicam a funcionário) */}
      {!query && reaches("admin") && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {TIP_KEYS.map(({ key, icon: Icon }) => (
            <Card key={key} className="border-border/70">
              <CardContent className="flex items-start gap-3 p-4">
                <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Icon className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-sm font-semibold">{t(`help.tips.${key}.title`)}</p>
                  <p className="text-xs text-muted-foreground">{t(`help.tips.${key}.desc`)}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Separator />

      {/* Categories */}
      {filtered.length === 0 ? (
        <p className="py-12 text-center text-muted-foreground">
          {t("help.noSearchResults", { query: search })}
        </p>
      ) : (
        <Accordion
          type="multiple"
          defaultValue={query ? filtered.map((c) => c.key) : []}
          className="space-y-3"
        >
          {filtered.map((cat) => {
            const CatIcon = CATEGORY_ICONS[cat.key] || Info;
            return (
              <AccordionItem
                key={cat.key}
                value={cat.key}
                className="rounded-lg border border-border/70 px-1"
              >
                <AccordionTrigger className="px-4 py-3 hover:no-underline">
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <CatIcon className="h-5 w-5" />
                    </div>
                    <span className="text-left text-base font-semibold">{cat.title}</span>
                    <Badge variant="secondary" className="ml-1 text-[10px]">
                      {cat.items.length}
                    </Badge>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4">
                  <div className="space-y-3">
                    {cat.items.map((item) => {
                      const ItemIcon = ITEM_ICONS[item.key] || Info;
                      return (
                        <Card
                          key={item.key}
                          className="border-border/60 transition-all hover:border-primary/30 hover:shadow-sm"
                        >
                          <CardHeader className="pb-3">
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex items-center gap-2">
                                <ItemIcon className="h-4 w-4 text-primary" />
                                <CardTitle className="text-sm">{item.title}</CardTitle>
                              </div>
                              {item.link && (
                                <Link
                                  to={item.link as "/clients"}
                                  className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-primary hover:underline"
                                >
                                  {t("help.open")} <ArrowRight className="h-3 w-3" />
                                </Link>
                              )}
                            </div>
                            <CardDescription className="text-xs">{item.desc}</CardDescription>
                          </CardHeader>
                          <CardContent className="pt-0">
                            <ul className="space-y-1.5">
                              {item.details.map((d, i) => (
                                <li
                                  key={i}
                                  className="flex items-start gap-2 text-sm text-muted-foreground"
                                >
                                  <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary/70" />
                                  <span>{d}</span>
                                </li>
                              ))}
                            </ul>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      )}

      {/* Footer note */}
      <Card className="border-primary/30 bg-primary/5">
        <CardContent className="p-5">
          <div className="flex items-start gap-3">
            <Lightbulb className="mt-0.5 h-5 w-5 text-primary" />
            <div>
              <p className="text-sm font-semibold">{t("help.finalTipTitle")}</p>
              <p className="text-sm text-muted-foreground">{t("help.finalTipDesc")}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
